import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { GlossaryTerm } from '@/types/glossary';

export const GLOSSARY_KEY = ['glossary'] as const;

export function useGlossary() {
  return useQuery({
    queryKey: GLOSSARY_KEY,
    queryFn: () => apiFetch<GlossaryTerm[]>('/glossary'),
  });
}

interface CreateInput {
  term: string;
  preserve_as_is?: boolean;
  notes?: string | null;
}
interface UpdateInput {
  term?: string;
  preserve_as_is?: boolean;
  notes?: string | null;
}

export function useCreateTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInput) =>
      apiFetch<GlossaryTerm>('/glossary', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GLOSSARY_KEY });
      qc.invalidateQueries({ queryKey: ['glossary-stats'] });
    },
  });
}

export function useUpdateTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateInput }) =>
      apiFetch<GlossaryTerm>(`/glossary/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GLOSSARY_KEY });
      qc.invalidateQueries({ queryKey: ['glossary-stats'] });
    },
  });
}

export function useDeleteTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ status: 'deleted' }>(`/glossary/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GLOSSARY_KEY });
      qc.invalidateQueries({ queryKey: ['glossary-stats'] });
    },
  });
}

export function useUpsertTermTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      termId,
      lang,
      translation,
    }: {
      termId: string;
      lang: string;
      translation: string;
    }) =>
      apiFetch(`/glossary-translations/${termId}/${encodeURIComponent(lang)}`, {
        method: 'PUT',
        body: JSON.stringify({ translation }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GLOSSARY_KEY });
      qc.invalidateQueries({ queryKey: ['glossary-stats'] });
    },
  });
}
